import { useState, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { Page, PageHeader, PageList, PageSearch, SnackbarPopup } from 'frame/components'
import Columns from './columns'
import Options from './list-options'
import { useDispatch } from 'react-redux'
import { actions as fromSettings } from 'features/settings'
import qs from 'query-string'
import { useLocation } from 'react-router-dom'
import SkeletonTable from './skeleton-table'
import Stack from '@mui/material/Stack'

const List = ({
  config,
  actions,
  filters,
  fleetId,
  model,
  renderItem,
  routes,
  maskComponent,
  customActions,
  documentPreviewUri,
  renderViewAllRow,
  documentListClass,
  actionButtons,
}) => {

  const dispatch = useDispatch()
  const location = useLocation()

  const [delayLoading, setDelayLoading] = useState(!!config.delayLoading)

  const ChildRoutes = routes
  const Filters = filters

  const hideButtons = config?.hideButtons || false
  const hideSearch = config?.hideSearch || false
  const noDownload = config?.noDownload || false
  const noColumnSort = config?.noColumnSort || false
  const noPagination = config?.noPagination || false
  const noShowingResults = config?.noShowingResults || false
  const showSearchOnTableHeader = config?.showSearchOnTableHeader || false

  useEffect(() => {
    if (config.delayLoading) {
      setTimeout(() => setDelayLoading(false), config.delayLoading)
    }

    const queryParams = { FleetId: fleetId }
    filters && dispatch(actions.requestFilters(null, { queryParams }))

    const urlParams = qs.parse(location.search)
    const isDashboardFiltered = urlParams && Object.keys(urlParams).length > 0

    !isDashboardFiltered && dispatch(actions.fetchList())

    dispatch(fromSettings.setCurrentModule(actions.currentPageModule))

    return () => {
      dispatch(actions.clearFilters())
      dispatch(actions.clearSearchKey())
    }
  }, []) // eslint-disable-line

  const methods = useMemo(() => {
    const result = {}
    Object.keys(actions).forEach((key) => {
      result[key] = (...args) => dispatch(actions[key](...args))
    })
    return result
  }, [actions, dispatch])

  const loaded = model.flags?.list?.status === 'success'
  const loading = model.flags?.list?.status === 'loading'
  const error = model.flags?.list?.status === 'error'
  const errorMessage = model.flags?.list?.errorMessage
  const total = noPagination ? Object.entries(model.data).length : model.list?.size || 0

  const listed = [...new Set(model?.list?.ids)]
    .map(nodeId => model.data?.[nodeId])
    .filter(x => x)

  const listable = loaded && listed ? listed : []

  const headers = config.headers.map(node => ({
    ...node,
    visible: model.columns[node.key],
    showToggle: config?.model?.find(x => x.key === node.key)?.column?.showToggle,
    toolTip: config?.model?.find(x => x.key === node.key)?.column?.toolTip,
    toggleAction: config?.model?.find(x => x.key === node.key)?.column?.toggleAction,
    toggleValue: config?.model?.find(x => x.key === node.key)?.column?.toggleValue,
  }))

  const Banner = () => (
    <Columns
      config={config}
      columns={model.columns}
      onToggleColumn={methods.toggleColumn}
      onSelectAllColumns={methods.toggleSelectAllColumns}/>
  )

  const onDownload = (selected, allColumns) => {
    const columnsForDownload = { ...model.columns }

    const updatedColumns = Object.keys(model.columns).reduce((acc, key) => {
      acc[key] = true
      return acc
    }, {})

    methods.onDownload({
      columns: selected && allColumns && window.location.pathname.includes('/fbt/summary')
        ? updatedColumns
        : columnsForDownload,
      selected,
      fleetId,
      title: config.title
    })
  }

  const onRefresh = () => {
    if (customActions?.onRefetch) {
      customActions.onRefetch()
    } else {
      dispatch(actions.clearFilters())
      const queryParams = { FleetId: fleetId }
      dispatch(actions.requestFilters(null, { queryParams }))
    }
  }

  const onCheckAll = (selectedAll) => selectedAll
    ? dispatch(actions.selectRow(listable.map(data => data.uniqueId)))
    : dispatch(actions.selectRow([]))

  return (
    <Page title={config.title} sx={{ px: 3, width: '100%', minWidth: 0 }}>
      <Stack my={2} sx={{ width: '100%', minWidth: 0 }}>
        <Stack
          flexDirection={{ xs: 'column', md: 'row' }}
          alignItems={{ xs: 'stretch', md: 'center' }}
          justifyContent="space-between"
          gap={2}
          sx={{ width: '100%', minWidth: 0 }}>
          {!hideSearch && !maskComponent && !delayLoading && !showSearchOnTableHeader && (
            <PageSearch
              searchKey={model.searchKey}
              onSearch={methods.changeSearchKey}/>
          )}
          {actionButtons && actionButtons}
        </Stack>

        {config?.breadcrumbs?.path && (
          <PageHeader
            breadcrumbs={config?.breadcrumbs?.label || config?.breadcrumbs}
            breadcrumbsLink={config?.breadcrumbs?.path}/>
        )}

        {filters && !hideButtons && <Filters />}

        {delayLoading ? (
          <SkeletonTable />
        ) : maskComponent ? (
          maskComponent
        ) : (
          <PageList
            customComponent={showSearchOnTableHeader ? (
              <PageSearch
                searchKey={model.searchKey}
                onSearch={methods.changeSearchKey}/>
            ) : undefined}

            actions={hideButtons ? null : (
              <Options
                columns={model.columns}
                filtering={model.filtering}
                hideFilterLabel={config?.disabledClearFilter}
                onClearFilters={customActions?.onClearFilters || methods.clearFilters}
                onShowFilters={filters ? methods.showFilters : null}
                onRefresh={onRefresh}
                onDownload={noDownload ? null : onDownload}
                onToggleShowColumns={methods.toggleShowColumns}/>
            )}

            banner={<Banner />}
            sx={!documentListClass ? { marginTop: 3 } : documentListClass}
            error={error}
            errorMessage={errorMessage}
            disclaimerMessage={config.disclaimerMessage}
            noRecordsMessage={config.noRecordsMessage}
            loading={loading}
            title={config?.tableName || config.title}
            subtitle={config.subtitle}
            headers={headers}
            listable={listable}
            hideFilterLabel={config?.disabledClearFilter}
            useDocumentPanel={!!documentPreviewUri}

            offset={model.pagination.offset}
            rows={model.pagination.rows}
            total={total}
            onChangePaginationOffset={methods.setPaginationOffset}
            onChangePaginationRows={methods.setPaginationRows}

            noColumnSort={noColumnSort}
            selected={model.selected}
            columnsSort={model.columnsSort}
            filtering={model.filtering}
            showColumns={model.showColumns}
            onToggleColumnsSort={methods.toggleColumnSort}
            onCheckAll={onCheckAll}
            noPagination={noPagination}
            noShowingResults={noShowingResults}>
            {listable.map((item, index) =>
              renderItem(item, index, {
                columns: model.columns,
                selected: model.selected,
              })
            )}

            {renderViewAllRow && renderViewAllRow(listable)}
          </PageList>
        )}

        {routes && <ChildRoutes />}

        {model?.downloadStatus && (
          <SnackbarPopup
            coloured
            success={model.downloadStatus.toLowerCase().includes('success')}
            error={model.downloadStatus.toLowerCase().includes('error')}
            message={model.downloadStatus}/>
        )}
      </Stack>
    </Page>
  )
}

List.propTypes = {
  actions: PropTypes.any.isRequired,
  renderViewAllRow: PropTypes.func,
  config: PropTypes.any.isRequired,
  filters: PropTypes.any,
  fleetId: PropTypes.string.isRequired,
  model: PropTypes.any.isRequired,
  renderItem: PropTypes.func.isRequired,
  routes: PropTypes.any,
  maskComponent: PropTypes.any,
  customActions: PropTypes.object,
  documentPreviewUri: PropTypes.string,
}

export default List
